import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, LOCALE_ID, PLATFORM_ID, WritableSignal, afterNextRender, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { PageHeaderComponent } from '@stair-craft/common/components';

const MAX_CALCULATIONS: number = 5000;
const JITTER_FRACTION: number = 0.2;
const BEM_BLOCK: string = 'sc-about-page';

@Component({
    selector: 'sc-about-page',
    templateUrl: './about-page.component.html',
    styleUrl: './about-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        RouterLink,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        PageHeaderComponent,
    ],
    host: {
        class: BEM_BLOCK,
    },
})
export default class AboutPageComponent {
    readonly #localeId: string = inject(LOCALE_ID);
    readonly #platformId: object = inject(PLATFORM_ID);

    protected readonly calcCount: WritableSignal<string> = signal<string>('—');
    protected readonly monthYear: WritableSignal<string> = signal<string>('—');

    constructor() {
        afterNextRender((): void => {
            if (!isPlatformBrowser(this.#platformId)) {
                return;
            }
            const now: Date = new Date();
            const day: number = now.getDate();
            const daysInMonth: number = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            const fraction: number = day / daysInMonth;
            const base: number = MAX_CALCULATIONS * fraction;
            // eslint-disable-next-line sonarjs/pseudo-random -- декоративный джиттер счётчика на витрине, не security-sensitive
            const jitter: number = (Math.random() - 0.5) * 2 * JITTER_FRACTION * base;
            const count: number = Math.max(0, Math.round(base + jitter));
            this.calcCount.set(new Intl.NumberFormat(this.#localeId).format(count));

            // Format with day+month+year so locales that decline months (e.g. ru-RU)
            // produce the genitive case ("сентября" not "сентябрь"). Then pull just
            // the month + year parts via formatToParts.
            const parts: Intl.DateTimeFormatPart[] = new Intl.DateTimeFormat(this.#localeId, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).formatToParts(now);
            const month: string = parts.find((p: Intl.DateTimeFormatPart): boolean => p.type === 'month')?.value ?? '';
            const year: string = parts.find((p: Intl.DateTimeFormatPart): boolean => p.type === 'year')?.value ?? '';
            this.monthYear.set(`${month} ${year}`.trim());
        });
    }
}
